import { useCallback, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { LobbyRegistrationBanner } from './components/LobbyRegistrationBanner';
import { RegBanner, RegSlideProps } from './components/RegBanner';
import {
  DESK_ESTONIA_HEIGHT,
  DESK_HEIGHT_BANNER,
  getSlideHeight,
  MOBILE_ESTONIA,
  MOBILE_HEIGHT,
} from './helpers';
import { useIsLobbyFocusOnWelcomeAndRegABTest } from './hooks';
import { RegistrationForm } from '@/components/dialogs/Registration/components/RegistrationForm';
import { NativeSlider } from '@/components/NativeSlider';
import { SliderArrow } from '@/components/NativeSlider/helpers';
import { useResizeObserver } from '@/hooks';
import { useDevice } from '@/hooks/device';
import useEstonia from '@/hooks/estonia';
import { useCommonStore } from '@/store';

import { ScRegFormAbsoluteWrapper } from './components/RegBanner/styled';
import { ScSliderContainer } from './styled';
import { ScContainer } from '@/styles/common';

export const RegistrationSlider = () => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [elementSize, setElementSize] = useState(0);

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    setElementSize(entry.contentRect.height);
  }, []);

  useResizeObserver(elementRef, handleResize);

  const { regSlides, availableCrossSell, registrationBannerData } =
    useCommonStore(
      useShallow((state) => ({
        regSlides: state.regSlides,
        availableCrossSell: state.availableCrossSell,
        registrationBannerData: state.registrationBannerData,
      }))
    );
  const { isEstonia } = useEstonia();

  const { isMobileDevice: isMobile } = useDevice();

  const isLobbyRegistrationBannerABTest =
    useIsLobbyFocusOnWelcomeAndRegABTest();

  const normalizedRegSlides = useMemo((): RegSlideProps[] => {
    if (!regSlides) return [];

    return regSlides.map(
      (
        {
          charactersDesktop,
          backgroundDesktop,
          backgroundMobile,
          charactersMobile,
          title,
          titleColor,
          benefits,
        },
        index
      ) => {
        const advantages =
          benefits?.map(({ text, crossSellText, icon }) => ({
            text: availableCrossSell && crossSellText ? crossSellText : text,
            icon: icon?.url,
          })) || [];

        const slideHeight = getSlideHeight(
          elementSize,
          !!isMobile,
          isEstonia,
          MOBILE_HEIGHT,
          MOBILE_ESTONIA,
          DESK_HEIGHT_BANNER,
          DESK_ESTONIA_HEIGHT
        );

        return {
          backgroundDesktop: backgroundDesktop?.url,
          backgroundMobile: backgroundMobile?.url,
          characterImage: isMobile
            ? charactersMobile?.url
            : charactersDesktop?.url,
          title,
          titleColor,
          advantages,
          height: slideHeight,
          priority: index === 0,
          loading: index === 0 ? 'eager' : 'lazy',
        };
      }
    );
  }, [regSlides, isMobile, elementSize, isEstonia, availableCrossSell]);

  const commonSliderProps = useMemo(
    () => ({
      loop: true,
      arrows: {
        type: SliderArrow.INSIDE,
        insetLeftArrow: isMobile ? '191px auto auto 32px' : undefined,
        insetRightArrow: isMobile ? '191px 32px auto auto' : undefined,
        visibleForMobile: true,
      },
      autoplay: {
        delay: 8000,
        pauseOnMouseEnter: true,
      },
    }),
    [isMobile]
  );

  const regForm = (
    <RegistrationForm
      formId="one-step-reg"
      isOneStepRegistration
      openInnerModalsInPortal
    />
  );

  return (
    <ScSliderContainer
      isLobbyRegistrationBannerABTest={isLobbyRegistrationBannerABTest}
      registrationBannerUrl={registrationBannerData?.background.url || ''}
    >
      {!isLobbyRegistrationBannerABTest && (
        <NativeSlider<RegSlideProps>
          slide={RegBanner}
          slides={normalizedRegSlides}
          {...commonSliderProps}
        />
      )}
      {(!isEstonia || isLobbyRegistrationBannerABTest) && (
        <ScContainer>
          <ScRegFormAbsoluteWrapper
            ref={elementRef}
            bannerHeight={elementSize}
            isLobbyRegistrationBannerABTest={isLobbyRegistrationBannerABTest}
          >
            {isLobbyRegistrationBannerABTest ? (
              <LobbyRegistrationBanner />
            ) : (
              regForm
            )}
          </ScRegFormAbsoluteWrapper>
        </ScContainer>
      )}
    </ScSliderContainer>
  );
};
